import React, { useState, useEffect, useRef } from 'react';
const API_URL = process.env.REACT_APP_API_URL || '';

export default function StopLocationSelect({ value, onChange, label = 'Stop Location', disabled }) {
  const [query, setQuery] = useState(value || '');
  const [options, setOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLoc, setNewLoc] = useState({ name: '', address1: '', city: '', state: '', zip_code: '', phone: '', contact_name: '' });
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { setQuery(value || ''); }, [value]);

  const search = async (q) => {
    setLoading(true);
    try {
      const url = q ? `${API_URL}/api/stop-locations?q=${encodeURIComponent(q)}` : `${API_URL}/api/stop-locations`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setOptions(Array.isArray(data) ? data.slice(0, 8) : []);
    } catch { setOptions([]); }
    setLoading(false);
  };

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setShowDropdown(true);
    search(v);
    onChange(null, v, '', '', '', '', '', '', '', '', '', '');
  };

  const select = (opt) => {
    setQuery(opt.name);
    setShowDropdown(false);
    const addr = [opt.address1, opt.address2].filter(Boolean).join(', ');
    onChange(opt.id, opt.name, addr, opt.city || '', opt.state || '', opt.zip_code || '', opt.phone || '', opt.fax || '', opt.email || '', opt.contact_name || '', opt.type_of_record || '', opt.notes || '');
  };

  const addNew = async () => {
    if (!newLoc.name.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/stop-locations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newLoc)
      });
      const created = await res.json();
      select(created);
      setShowAddForm(false);
      setNewLoc({ name: '', address1: '', city: '', state: '', zip_code: '', phone: '', contact_name: '' });
    } catch(err) { console.error(err); }
  };

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <div className="relative">
        <input
          value={query}
          onChange={handleInput}
          onFocus={() => { setShowDropdown(true); if (options.length === 0) search(query); }}
          disabled={disabled}
          placeholder="Type to search or select..."
          className={`w-full px-2 py-1.5 pr-8 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${disabled ? 'bg-gray-50' : ''}`}
        />
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); setShowDropdown(v => !v); if (!showDropdown) search(query); }}
          disabled={disabled}
          className="absolute right-1 top-1/2 -translate-y-1/2 px-1 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      {showDropdown && (
        <div className="absolute z-50 w-full bg-white border border-gray-200 rounded shadow-lg mt-1 max-h-56 overflow-y-auto">
          {loading && <div className="px-3 py-2 text-xs text-gray-400">Searching...</div>}
          {!loading && options.map(opt => (
            <div key={opt.id} onMouseDown={() => select(opt)} className="px-3 py-2 hover:bg-blue-50 cursor-pointer">
              <div className="text-sm font-medium">{opt.name}</div>
              {(opt.city || opt.state) && <div className="text-xs text-gray-400">{[opt.address1, opt.city, opt.state].filter(Boolean).join(', ')}</div>}
            </div>
          ))}
          {!loading && options.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No matches — type to search</div>}
          <div className="border-t px-3 py-2">
            {!showAddForm ? (
              <button onMouseDown={() => setShowAddForm(true)} className="text-xs text-blue-600 hover:underline">+ Add New Stop Location</button>
            ) : (
              <div className="space-y-1">
                <input value={newLoc.name} onChange={e => setNewLoc(p => ({...p, name: e.target.value}))} placeholder="Name *" className="w-full px-2 py-1 text-xs border rounded" />
                <input value={newLoc.address1} onChange={e => setNewLoc(p => ({...p, address1: e.target.value}))} placeholder="Address" className="w-full px-2 py-1 text-xs border rounded" />
                <div className="flex gap-1">
                  <input value={newLoc.city} onChange={e => setNewLoc(p => ({...p, city: e.target.value}))} placeholder="City" className="flex-1 px-2 py-1 text-xs border rounded" />
                  <input value={newLoc.state} onChange={e => setNewLoc(p => ({...p, state: e.target.value}))} placeholder="ST" className="w-12 px-2 py-1 text-xs border rounded" />
                  <input value={newLoc.zip_code} onChange={e => setNewLoc(p => ({...p, zip_code: e.target.value}))} placeholder="Zip" className="w-16 px-2 py-1 text-xs border rounded" />
                </div>
                <input value={newLoc.phone} onChange={e => setNewLoc(p => ({...p, phone: e.target.value}))} placeholder="Phone" className="w-full px-2 py-1 text-xs border rounded" />
                <input value={newLoc.contact_name} onChange={e => setNewLoc(p => ({...p, contact_name: e.target.value}))} placeholder="Contact Name" className="w-full px-2 py-1 text-xs border rounded" />
                <div className="flex gap-1">
                  <button onMouseDown={addNew} className="px-2 py-1 text-xs bg-blue-600 text-white rounded">Add</button>
                  <button onMouseDown={() => setShowAddForm(false)} className="px-2 py-1 text-xs bg-gray-200 rounded">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
